'use client';

import { FC, useState } from 'react';
import { css } from '@bts-web/utils-style-engine';
import { DatadogErrorLevels } from '@bts-web/utils-next-infrastructure';
import { Locale, useClientTranslation } from '@bts-web/utils-lokalise';
import { PreAcceptSavingsPlanMutation$data } from '@bts-web/data-layer/server';
import {
  capitalizeFirstLetter,
  getPrecisionFloatValue,
} from '@bts-web/utils-formatting';
import { convertToLocaleDateString } from '@bts-web/utils-formatting-client';
import { isBpfsAsset } from '../utils/isBpfsAsset';
import { AssetType } from '../utils';
import { PoweredBy } from '../Reusable/PoweredBy/PoweredBy';
import { TradeScreenTranslations } from '../utils/getTradeTranslations';
import {
  AmountInputWrapper,
  ButtonBase,
  datadogErrorHelper,
  useHandleGqlErrorsWithSnackbar,
} from '../../common';
import { BelowInputInfoTable } from '../../common/components/BelowInputInfoTable/BelowInputInfoTable';
import { acceptSavingsPlanAction } from './actions/acceptSavingsPlan.action';
import { getConvertedSummary } from '../CreateTrade/utils/getConvertedSummary';
import { SavingsPlanInfo } from '../../savings-plans/savings-plan-details/components/SavingsPlanInfo/SavingsPlanInfo';
import { getAppConfig } from '@bts-web/core-app-config';
import { TradeAssetInfoDisplay } from '../../common/components/TradeAssetInfoDisplay/TradeAssetInfoDisplay';
import {
  finishTradeRedirectBuilder,
  IFinishTradeRedirectBuilderProps,
} from '../FinishTrade/actions/finishTradeRedirectBuilder.action';
import { useAppNotification } from '../../notifications';
import { FilledSpinnerIos } from '../../common/svgIcons';
import { MutableNonNullableAsset } from '../CreateTrade/types';

// THIS FUNCTION is required to be executed outside of a try-catch block, because a redirect would trigger a fail in the catch block
const executeRedirect = (props: IFinishTradeRedirectBuilderProps) => {
  finishTradeRedirectBuilder(props);
};

export type AcceptSavingsPlanProps = {
  savingsPlanData: NonNullable<
    PreAcceptSavingsPlanMutation$data['preAcceptSavingsPlan']
  >;
  translations: TradeScreenTranslations;
  currentLocale: Locale;
};

export const AcceptSavingsPlan: FC<AcceptSavingsPlanProps> = ({
  translations,
  currentLocale,
  savingsPlanData,
}) => {
  const { setAppNotification } = useAppNotification();

  const { processErrors } = useHandleGqlErrorsWithSnackbar();

  const { t } = useClientTranslation();

  const [isConfirmLoading, setIsConfirmLoading] = useState(false);

  const [isConfirmDone, setIsConfirmDone] = useState(false);

  const {
    priceBuy,
    symbol: initialSymbol,
    __typename,
  } = savingsPlanData.asset as NonNullable<typeof savingsPlanData.asset>;

  const symbol =
    __typename === 'MetalAsset' ? translations.grams : initialSymbol;

  const totalSum = getPrecisionFloatValue(savingsPlanData.fiatAmount);

  const isBpf = isBpfsAsset(__typename as AssetType);

  const poweredByText = isBpf ? translations.poweredByBpfs : '';

  const { currency, paymentDaysBeforeExecution = 3 } = getAppConfig();

  const triggerAcceptSavingsPlan = async () => {
    setIsConfirmLoading(true);

    try {
      const response = await acceptSavingsPlanAction({
        savingsPlanId: savingsPlanData.id,
      });

      if (response.errors && response.errors.length > 0) {
        const errorCodeString = response.errors.join(',');

        const isDuplicateError = errorCodeString.includes('duplicate');

        if (isDuplicateError) {
          setAppNotification({
            position: 'topRight',
            title: translations.errorMessageDuplicateSavingsPlan,
            type: 'toast',
            visual: 'error',
            withClose: true,
          });
        } else {
          processErrors(response.errors);
        }

        // Error case - reset states and exit
        setIsConfirmLoading(false);

        setIsConfirmDone(false);

        return;
      }

      // Check for successful response data
      if (!response.data || !response.data.acceptSavingsPlan) {
        throw new Error('server failure: missing response data');
      }

      // Success case

      executeRedirect({
        assetId: savingsPlanData.asset?.id as string,
        offerId: response.data.acceptSavingsPlan.id as string,
        transactionStatus: response.data.acceptSavingsPlan.status,
        variant: 'savings',
      });
    } catch (error) {
      // Reset loading states
      setIsConfirmLoading(false);

      setIsConfirmDone(false);

      processErrors(
        typeof error === 'string'
          ? [error]
          : typeof error === 'object' &&
              error &&
              'errors' in error &&
              Array.isArray((error as any).errors)
            ? (error as any).errors
            : [],
      );

      // Log to Datadog
      datadogErrorHelper({
        errorMessage:
          error instanceof Error ? error.message : JSON.stringify(error),
        errorSeverity: DatadogErrorLevels.CRITICAL,
      });
    }
  };

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        px: 'medium',
        height: '100%',
        gap: 'medium',
      })}
    >
      {/* Special Input */}
      <div>
        <TradeAssetInfoDisplay
          sourceAssetData={savingsPlanData.asset as MutableNonNullableAsset}
          price={savingsPlanData.asset?.priceBuy}
          symbol={savingsPlanData.asset?.symbol as string}
          operation="savings"
        />

        <AmountInputWrapper
          inputContextData={{
            symbol: symbol as string,
            currency,
            displayType: 'FIAT',
            value: totalSum,
            placeholder: undefined,
          }}
        />

        <div
          className={css({
            pt: 'extra_small',
            pb: 'medium',
            borderBottom: '1px solid',
            borderBottomColor: 'neutrals.divider',
          })}
        >
          <BelowInputInfoTable
            items={[
              {
                title: capitalizeFirstLetter(translations['units'], true),
                value: getConvertedSummary({
                  assetPrice: priceBuy,
                  assetType: savingsPlanData.asset?.__typename as AssetType,
                  tradeType: 'savings',
                  currency,
                  displayType: 'FIAT',
                  locale: currentLocale,
                  symbol: symbol,
                  translations,
                  value: totalSum,
                }),
              },
            ]}
          />
        </div>
      </div>

      <BelowInputInfoTable
        items={[
          {
            title: translations.frequency,
            value: savingsPlanData.frequency
              ? translations[savingsPlanData.frequency]
              : '',
          },
          ...(savingsPlanData.nextRecurrence
            ? [
                {
                  title: translations.startsAt,
                  value: convertToLocaleDateString(
                    savingsPlanData.nextRecurrence,
                    currentLocale,
                  ),
                },
              ]
            : []),
          ...(savingsPlanData.notificationDate
            ? [
                {
                  title: translations.nextPayment,
                  value: convertToLocaleDateString(
                    savingsPlanData.notificationDate,
                    currentLocale,
                  ),
                },
              ]
            : []),
        ]}
      />

      <SavingsPlanInfo
        label={t('savings_plan_info_label_@tenant', {
          days: paymentDaysBeforeExecution,
        })}
      />

      <div
        className={css({
          pb: 'medium',
          marginTop: 'auto',
        })}
      >
        <PoweredBy
          value={isBpf ? poweredByText : t('powered_by_@tenant')}
          showLogo={!isBpf}
        />

        <ButtonBase
          data-testid="confirm-button"
          visual="primary"
          size="large"
          disabled={isConfirmLoading || isConfirmDone}
          onClick={triggerAcceptSavingsPlan}
          fullWidth
        >
          {translations.confirm}

          {isConfirmLoading && (
            <FilledSpinnerIos
              aria-label="loading icon"
              data-element="loading-icon"
            />
          )}
        </ButtonBase>
      </div>
    </div>
  );
};
